import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ExternalLink,
  Copy,
  Check,
  Download,
  Settings2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { legacyRoutes } from '@/lib/legacy';
import { SensorStatusBadge } from './SensorStatusBadge';
import type { InactiveSensor } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

export interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

export type SortKey = 'name' | 'organisation.name' | 'timestamp' | 'daysInactive' | 'status' | 'billable';

// =============================================================================
// Helpers
// =============================================================================

function calculateDaysInactive(timestamp: string | null): number | null {
  if (!timestamp) return null;
  const lastReading = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - lastReading.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0"
      onClick={handleCopy}
      aria-label={label}
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" />
      )}
    </Button>
  );
}

// =============================================================================
// Column Definitions
// =============================================================================

export const COLUMNS: ColumnDef<InactiveSensor>[] = [
  {
    key: 'name',
    label: 'Sensor',
    sortKey: 'name',
    filterType: 'text',
    getValue: (s) => s.name,
    render: (s) => (
      <div className="flex items-center gap-1 font-medium">
        {s.name}
        <CopyButton text={s.name} label={`Copy sensor name: ${s.name}`} />
      </div>
    ),
  },
  {
    key: 'stream',
    label: 'Stream',
    filterType: 'text',
    getValue: (s) => s.stream?.name || '',
    render: (s) =>
      s.stream?.name ? (
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">{s.stream.name}</span>
          <CopyButton
            text={s.stream.name}
            label={`Copy stream: ${s.stream.name}`}
          />
        </div>
      ) : null,
  },
  {
    key: 'customer',
    label: 'Account',
    sortKey: 'organisation.name',
    filterType: 'text',
    getValue: (s) => s.organisation?.name || '',
    render: (s) =>
      s.organisation ? (
        <Link
          to={`/accounts/${s.organisation._id}`}
          className="text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {s.organisation.name}
        </Link>
      ) : (
        <span className="text-muted-foreground">No customer</span>
      ),
  },
  {
    key: 'quote',
    label: 'Quote #',
    filterType: 'text',
    getValue: (s) => s.quoteNumbers?.join(', ') || '',
    render: (s) =>
      s.quoteNumbers && s.quoteNumbers.length > 0 ? (
        <span className="text-sm font-mono">
          {s.quoteNumbers.join(', ')}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'site',
    label: 'Site / Location',
    filterType: 'text',
    getValue: (s) =>
      [s.site?.name, s.location?.name].filter(Boolean).join(' / '),
    render: (s) => (
      <div className="text-sm">
        {s.site?.name || (
          <span className="text-muted-foreground">&mdash;</span>
        )}
        {s.location && (
          <span className="text-muted-foreground">
            {' / '}
            {s.location.name}
          </span>
        )}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    filterType: 'select',
    filterOptions: [
      { label: 'Inactive', value: '5' },
    ],
    getValue: (s) => String(s.status),
    render: (s) => <SensorStatusBadge status={s.status} />,
  },
  {
    key: 'lastReading',
    label: 'Last Packet',
    sortKey: 'timestamp',
    filterType: 'date-range',
    getValue: (s) =>
      s.timestamp
        ? new Date(s.timestamp).toLocaleDateString('en-CA')
        : '',
    render: (s) => formatDate(s.timestamp),
  },
  {
    key: 'daysInactive',
    label: 'Days Inactive',
    sortKey: 'daysInactive',
    filterType: 'number-range',
    getValue: (s) => calculateDaysInactive(s.timestamp),
    render: (s) => {
      const days = calculateDaysInactive(s.timestamp);
      if (days === null)
        return <span className="text-muted-foreground">&mdash;</span>;
      const isLong = days > 30;
      return (
        <span
          className={
            isLong ? 'text-red-600 font-medium' : 'text-muted-foreground'
          }
        >
          {days} days
        </span>
      );
    },
  },
  {
    key: 'billable',
    label: 'Billable',
    sortKey: 'billable',
    filterType: 'select',
    filterOptions: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
    getValue: (s) => String(s.billable),
    render: (s) =>
      s.billable ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          Yes
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          No
        </span>
      ),
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (s) => (
      <a
        href={legacyRoutes.sensor(s._id)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <Button variant="ghost" size="sm">
          <ExternalLink className="h-4 w-4" />
        </Button>
      </a>
    ),
    align: 'right',
  },
];

// =============================================================================
// Column Visibility Helpers
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-inactive-sensors-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

export function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return getDefaultVisibility();
}

export function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter<T>(
  col: ColumnDef<T>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const optionValues = new Set(col.filterOptions.map((opt) => opt.value));
    const selected: string[] = (() => {
      try {
        const parsed = columnFilters[col.key]
          ? JSON.parse(columnFilters[col.key])
          : [];
        return Array.isArray(parsed)
          ? parsed.filter(
              (value): value is string =>
                typeof value === 'string' && optionValues.has(value)
            )
          : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {selected.length ? `${selected.length} selected` : '\u2014'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${to}`)
            }
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) =>
              setColumnFilter(col.key, `${from}>>${e.target.value}`)
            }
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${max}`)
            }
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) =>
              setColumnFilter(col.key, `${min}>>${e.target.value}`)
            }
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Table Component
// =============================================================================

interface InactiveSensorsTableProps {
  sensors: InactiveSensor[];
  isLoading: boolean;
  total: number;
  sortKey: string;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
  onSensorClick: (sensorId: string) => void;
  columnFilters: Record<string, string>;
  setColumnFilter: (key: string, value: string) => void;
  columnVisibility: Record<string, boolean>;
  toggleColumn: (key: string) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onExportCSV: () => void;
  onCopyClipboard: () => void;
}

export function InactiveSensorsTable({
  sensors,
  isLoading,
  total,
  sortKey,
  sortDirection,
  onSort,
  onSensorClick,
  columnFilters,
  setColumnFilter,
  columnVisibility,
  toggleColumn,
  page,
  totalPages,
  onPageChange,
  onExportCSV,
  onCopyClipboard,
}: InactiveSensorsTableProps) {
  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = COLUMNS.filter((col) => isVisible(col.key));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Showing {sensors.length} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onCopyClipboard}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={onExportCSV}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* ROW 1: Filter inputs */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(col, columnFilters, setColumnFilter)
                )}
              </TableRow>

              {/* ROW 2: Column labels + sort */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(col.align === 'right' && 'text-right')}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={onSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading &&
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {/* Empty state */}
              {!isLoading && sensors.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No sensors found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading &&
                sensors.map((sensor) => (
                  <TableRow
                    key={sensor._id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onSensorClick(sensor._id)}
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.className,
                        )}
                      >
                        {col.render
                          ? col.render(sensor)
                          : String(col.getValue(sensor) ?? '') || '\u2014'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
